import { Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type {
  AgentInfo,
  AgentSessionDetail,
  AgentSessionSummary,
  CopilotAgentMessage,
  PollAuthResult,
  StartAuthResponse,
} from "../../../api/copilotAgentTypes";
import { api } from "../../../api/operationsApi";
import { productionInvestigatorSlug } from "../observer/observerInvestigations";
import CopilotAgentChat from "./chat/CopilotAgentChat";
import { mapPollAuthError, modelsFallback, pickBestClaudeSonnet, resolveEffort } from "./copilotAgentsHelpers";
import GitHubAuthGate from "./GitHubAuthGate";
import SessionHistoryDrawer from "./SessionHistoryDrawer";

interface CopilotAgentPanelProps {
  agent: AgentInfo;
  draft: string;
  onDraftChange: (draft: string) => void;
}

interface InitialChatState {
  messages: CopilotAgentMessage[];
  usedSkills: string[];
  transcriptTruncated: boolean;
}

const getSession = withErrorHandling(api.copilotAgents.getSession);
const resumeSession = withErrorHandling(api.copilotAgents.resumeSession);
const listSessions = withErrorHandling(api.copilotAgents.listSessions);
const findSessionsByFirstMessage = withErrorHandling(api.copilotAgents.findSessionsByFirstMessage);
const startAuth = withErrorHandling(api.copilotAgents.startAuth);
const startInvestigation = withErrorHandling(api.observer.startInvestigation);

const mapStoredMessages = (detail: AgentSessionDetail): CopilotAgentMessage[] =>
  detail.messages.map((m) => ({
    id: m.messageId,
    role: m.role,
    content: m.content,
    reasoning: m.reasoning ?? undefined,
    usage: m.usage
      ? {
          inputTokens: m.usage.inputTokens,
          cachedInputTokens: m.usage.cachedInputTokens ?? undefined,
          outputTokens: m.usage.outputTokens,
          durationMs: m.usage.durationMs,
        }
      : undefined,
    timestamp: new Date(m.timestampUtc),
  }));

const CopilotAgentPanel = ({ agent, draft, onDraftChange }: CopilotAgentPanelProps) => {
  const { slug: agentSlug, defaultModel, defaultEffort } = agent;
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [searchParams] = useSearchParams();

  const [pendingAuth, setPendingAuth] = useState<StartAuthResponse>();
  const [model, setModel] = useState(() => defined(modelsFallback[0]).id);
  const [models, setModels] = useState<{ id: string; label: string }[]>(modelsFallback);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [effort, setEffort] = useState("");
  const [availableEfforts, setAvailableEfforts] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<string>();
  const [sessionKey, setSessionKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [initialState, setInitialState] = useState<InitialChatState>();
  const [sessions, setSessions] = useState<AgentSessionSummary[]>();
  const [sessionsLoadFailed, setSessionsLoadFailed] = useState(false);
  const [autoSendMessage, setAutoSendMessage] = useState<string>();
  const pendingResumeIdRef = useRef<string | null>(null);
  const pendingInvestigateRef = useRef<{ issueId: string; fileGithubIssue: boolean } | null>(null);
  const initialModelSelectedRef = useRef(false);
  const initialSessionStartedRef = useRef(false);
  const modelEffortsRef = useRef<Map<string, { supported: string[]; default: string | null }>>(new Map());

  const updateEffortForModel = useCallback(
    (modelId: string) => {
      const info = modelEffortsRef.current.get(modelId);
      setAvailableEfforts(info?.supported ?? []);
      setEffort(resolveEffort(info, defaultEffort) ?? "");
    },
    [defaultEffort]
  );

  const ensureModelsLoaded = useCallback(async (): Promise<{ model?: string; effort?: string }> => {
    if (initialModelSelectedRef.current) {
      return {};
    }

    setIsLoadingModels(true);
    try {
      const modelsRes = await api.copilotAgents.listModels();
      if (modelsRes.success && modelsRes.data.length > 0) {
        const modelList = modelsRes.data.map((m) => ({
          id: m.id,
          label: m.multiplier ? `${m.name} (${m.multiplier}x)` : m.name,
        }));
        setModels(modelList);
        modelEffortsRef.current = new Map(
          modelsRes.data.map((m) => [m.id, { supported: m.supportedEfforts ?? [], default: m.defaultEffort }])
        );
        const agentDefault = defaultModel ? modelList.find((m) => m.id === defaultModel) : undefined;
        const picked = agentDefault ?? pickBestClaudeSonnet(modelList);
        initialModelSelectedRef.current = true;
        if (picked) {
          setModel(picked.id);
          updateEffortForModel(picked.id);
          return { model: picked.id, effort: resolveEffort(modelEffortsRef.current.get(picked.id), defaultEffort) };
        }
      }
    } catch (err) {
      logError(err, "[CopilotAgentPanel] listModels");
    } finally {
      setIsLoadingModels(false);
    }

    return {};
  }, [defaultModel, defaultEffort, updateEffortForModel]);

  const tryCreateSession = useCallback(
    async (selectedModel?: string, selectedEffort?: string) => {
      const picked = await ensureModelsLoaded();
      const requestedModel = picked.model ?? selectedModel;
      const requestedEffort = picked.effort ?? selectedEffort;

      try {
        const res = await api.copilotAgents.createSession(agentSlug, requestedModel, requestedEffort || undefined);
        if (!res.success) {
          sendNotificationError("Failed to start the agent session.");
          return;
        }
        if (res.data.requiresGitHubAuth) {
          const authRes = await api.copilotAgents.startAuth();
          if (authRes.success) {
            setPendingAuth(authRes.data);
          }
        } else if (res.data.sessionId) {
          setSessionId(res.data.sessionId);
        }
      } catch (err) {
        logError(err, "[CopilotAgentPanel] createSession");
        sendNotificationError("Failed to start the agent session.");
      }
    },
    [agentSlug, ensureModelsLoaded, sendNotificationError]
  );

  const resetSession = useCallback(() => {
    setSessionId(undefined);
    setInitialState(undefined);
    setAutoSendMessage(undefined);
    setSessionKey((k) => k + 1);
  }, []);

  const handleModelChange = useCallback(
    (newModel: string) => {
      setModel(newModel);
      updateEffortForModel(newModel);
      resetSession();
      tryCreateSession(newModel, resolveEffort(modelEffortsRef.current.get(newModel), defaultEffort));
    },
    [tryCreateSession, updateEffortForModel, resetSession, defaultEffort]
  );

  const handleEffortChange = useCallback(
    (newEffort: string) => {
      setEffort(newEffort);
      resetSession();
      tryCreateSession(model, newEffort);
    },
    [model, tryCreateSession, resetSession]
  );

  const handleNewSession = useCallback(async () => {
    onDraftChange("");
    resetSession();
    await tryCreateSession(model);
  }, [model, tryCreateSession, resetSession, onDraftChange]);

  const handleResumeSession = useCallback(
    async (resumeId: string, autoSend?: string): Promise<boolean> => {
      setAutoSendMessage(autoSend);
      // Load the transcript before resumeSession switches the backend, so a failed load can't strand the chat.
      const [detail, detailError] = await getSession(resumeId);
      if (detailError) {
        logError(detailError, "[CopilotAgentPanel] getSession");
        sendNotificationError(detailError.message);
        return false;
      }

      const [resumed, resumeError] = await resumeSession(resumeId);
      if (resumeError) {
        logError(resumeError, "[CopilotAgentPanel] resumeSession");
        sendNotificationError(resumeError.message);
        return false;
      }

      if (resumed.requiresGitHubAuth) {
        const [auth, authError] = await startAuth();
        if (authError) {
          logError(authError, "[CopilotAgentPanel] startAuth");
          sendNotificationError(authError.message);
          return false;
        }
        pendingResumeIdRef.current = resumeId;
        setPendingAuth(auth);
        setHistoryOpen(false);
        return true;
      }

      await ensureModelsLoaded();
      if (resumed.model) {
        setModel(resumed.model);
        updateEffortForModel(resumed.model);
      }
      if (resumed.effort) {
        setEffort(resumed.effort);
      }
      setSessionId(resumed.sessionId ?? resumeId);
      setInitialState({
        messages: mapStoredMessages(detail),
        usedSkills: detail.usedSkills,
        transcriptTruncated: detail.summary.transcriptTruncated,
      });
      setSessionKey((k) => k + 1);
      setHistoryOpen(false);
      return true;
    },
    [ensureModelsLoaded, updateEffortForModel, sendNotificationError]
  );

  const investigateObserverIssue = useCallback(
    async (issueId: string, fileGithubIssue: boolean) => {
      // The mount effect runs this callback from the first render, so its captured model is still the
      // placeholder; recovery has to use what was just resolved.
      const picked = await ensureModelsLoaded();
      const recover = () => tryCreateSession(picked.model ?? model, picked.effort);

      const [started, error] = await startInvestigation(issueId, { fileGithubIssue });
      if (error) {
        logError(error, "[CopilotAgentPanel] startInvestigation");
        sendNotificationError(error.message);
        await recover();
        return;
      }

      if (started.requiresGitHubAuth) {
        const [auth, authError] = await startAuth();
        if (authError) {
          logError(authError, "[CopilotAgentPanel] startAuth");
          sendNotificationError(authError.message);
          await recover();
          return;
        }
        pendingInvestigateRef.current = { issueId, fileGithubIssue };
        setPendingAuth(auth);
        return;
      }

      if (!started.sessionId) {
        logError(new Error("Investigation started without a session"), "[CopilotAgentPanel] startInvestigation");
        sendNotificationError("Failed to start the investigation.");
        await recover();
        return;
      }

      if (started.githubIssueNumber) {
        sendNotification(`Filed GitHub issue #${started.githubIssueNumber}. The investigator will fill it in.`);
      }

      // A reused session has to be resumed first; it still gets a prompt when there is something new to ask,
      // which is what makes a filed issue reach an investigation that was already running.
      if (!started.sessionCreated) {
        await handleResumeSession(started.sessionId, started.prompt);
        return;
      }

      if (!started.prompt) {
        logError(new Error("Investigation started without a prompt"), "[CopilotAgentPanel] startInvestigation");
        sendNotificationError("Failed to start the investigation.");
        await recover();
        return;
      }

      setSessionId(started.sessionId);
      setAutoSendMessage(started.prompt);
    },
    [model, ensureModelsLoaded, handleResumeSession, tryCreateSession, sendNotification, sendNotificationError]
  );

  const openSessionForPrompt = useCallback(
    async (prompt: string) => {
      const [picked, [matches, error]] = await Promise.all([
        ensureModelsLoaded(),
        findSessionsByFirstMessage(agentSlug, prompt),
      ]);
      if (error) {
        logError(error, "[CopilotAgentPanel] findSessionsByFirstMessage");
      }

      const match = matches?.[0];
      if (match && (await handleResumeSession(match.sessionId))) {
        onDraftChange("");
        return;
      }

      await tryCreateSession(picked.model ?? model, picked.effort);
    },
    [agentSlug, model, ensureModelsLoaded, tryCreateSession, handleResumeSession, onDraftChange]
  );

  useEffect(() => {
    if (initialSessionStartedRef.current) {
      return;
    }
    initialSessionStartedRef.current = true;

    // The deep link is consumed once on mount; switching agents drops these params anyway.
    const isInvestigatorPanel = agentSlug === productionInvestigatorSlug;
    const deepLinkSessionId = isInvestigatorPanel ? searchParams.get("sessionId") : null;
    const investigateIssueId = isInvestigatorPanel ? searchParams.get("investigateIssueId") : null;
    const fileBugIssueId = isInvestigatorPanel ? searchParams.get("fileBugIssueId") : null;
    const initialPrompt = searchParams.get("initialPrompt")?.trim();
    if (fileBugIssueId) {
      investigateObserverIssue(fileBugIssueId, true);
    } else if (investigateIssueId) {
      investigateObserverIssue(investigateIssueId, false);
    } else if (deepLinkSessionId) {
      handleResumeSession(deepLinkSessionId);
    } else if (initialPrompt) {
      openSessionForPrompt(initialPrompt);
    } else {
      tryCreateSession(model);
    }
    // Intentionally no session deletion on unmount: sessions persist to history.
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- runs once on mount; model/handlers are captured deliberately
  }, []);

  const handlePollAuth = useCallback(async (): Promise<PollAuthResult> => {
    if (!pendingAuth) {
      return {
        authorized: false,
        continuePolling: false,
        message: "GitHub authorization is no longer active. Start again to retry.",
      };
    }

    const res = await api.copilotAgents.pollAuth(pendingAuth.deviceCode);
    if (!res.success) {
      return {
        authorized: false,
        continuePolling: false,
        message: "Failed to check GitHub authorization. Start again to retry.",
      };
    }
    if (!res.data.authorized) {
      return { authorized: false, ...mapPollAuthError(res.data.gitHubError, res.data.gitHubLogin) };
    }

    setPendingAuth(undefined);
    const pendingResumeId = pendingResumeIdRef.current;
    const pendingInvestigate = pendingInvestigateRef.current;
    pendingResumeIdRef.current = null;
    pendingInvestigateRef.current = null;
    if (pendingResumeId) {
      await handleResumeSession(pendingResumeId);
    } else if (pendingInvestigate) {
      await investigateObserverIssue(pendingInvestigate.issueId, pendingInvestigate.fileGithubIssue);
    } else {
      await tryCreateSession(model);
    }
    return { authorized: true, continuePolling: false };
  }, [pendingAuth, model, tryCreateSession, handleResumeSession, investigateObserverIssue]);

  const handleRestartAuth = useCallback(async () => {
    const res = await api.copilotAgents.startAuth();
    if (res.success) {
      setPendingAuth(res.data);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setSessions(undefined);
    setSessionsLoadFailed(false);
    const [loaded, error] = await listSessions(agentSlug);
    if (error) {
      logError(error, "[CopilotAgentPanel] listSessions");
      setSessionsLoadFailed(true);
      return;
    }
    setSessions(loaded);
  }, [agentSlug]);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    loadSessions();
  }, [loadSessions]);

  return (
    <Stack sx={{ height: "calc(100vh - 120px)" }}>
      {pendingAuth ? (
        <GitHubAuthGate
          agentDisplayName={agent.displayName}
          auth={pendingAuth}
          onPollAuth={handlePollAuth}
          onRestartAuth={handleRestartAuth}
        />
      ) : (
        <>
          <CopilotAgentChat
            key={sessionKey}
            agent={agent}
            sessionId={sessionId}
            isLoadingModels={isLoadingModels}
            model={model}
            models={models}
            onModelChange={handleModelChange}
            effort={effort}
            availableEfforts={availableEfforts}
            onEffortChange={handleEffortChange}
            onNewSession={handleNewSession}
            onOpenHistory={openHistory}
            initialMessages={initialState?.messages}
            initialUsedSkills={initialState?.usedSkills}
            initialTranscriptTruncated={initialState?.transcriptTruncated}
            autoSendMessage={autoSendMessage}
            draft={draft}
            onDraftChange={onDraftChange}
          />
          <SessionHistoryDrawer
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            onResume={handleResumeSession}
            sessions={sessions}
            loadFailed={sessionsLoadFailed}
            onReload={loadSessions}
          />
        </>
      )}
    </Stack>
  );
};

export default CopilotAgentPanel;
